import { useState } from "react";

import styles from "./Field.module.css";

/**
 * What a money field accepts: digits, at most two decimals behind a dot or
 * a comma, no sign. `checked_amount` on the server reads the same rule — a
 * negative amount is a discount nobody meant, a third decimal an amount the
 * total never forms exactly.
 */
const AMOUNT = /^\d+([.,]\d{1,2})?$/;
/** A figure still being typed — „12," — is not a refusal yet, and not a value either. */
const AMOUNT_UNFINISHED = /^\d+[.,]$/;

/**
 * A decimal field: text, decimal keyboard, checked **at the field**.
 *
 * The comma becomes a dot before `onChange`, and what does not read as a
 * figure at all is refused here: the field shows what was typed, marks it,
 * and hands nothing on. Empty is a value (`""`), not an error. The field
 * keeps its own draft while somebody types, so „12," stays on screen as
 * typed and a refused „abc" stays visible under its mark; a draft that reads
 * is dropped on blur and the stored figure shows again.
 *
 * Lifted from `PlanningScreen.tsx` (12.09.2026) with three optional props
 * the board never needed: `disabled`, an `error` the caller found on commit
 * (a margin outside 5–95 is a valid figure and still refused — by the
 * caller, in this line), and the `unitWord` the refusal names — „Betrag"
 * for money, „Prozentsatz" for a margin.
 */
export function MoneyField({
  id,
  label,
  value,
  hint,
  disabled = false,
  error = null,
  unitWord = "Betrag",
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  hint?: string | null;
  disabled?: boolean;
  error?: string | null;
  unitWord?: string;
  /** Called with the normalised figure — dot, never comma — or `""`. */
  onChange: (text: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const problem = refusal ?? error;
  // Named only while there is something to name — an `aria-describedby`
  // pointing at an absent id is worse than none (same rule `ApprovalPanel`'s
  // `reasonsId` already follows; fix round 2, item 10). The field carries a
  // real validation now (`ParameterBar`'s "Marge zwischen 5 und 95 %."), so a
  // screen reader hearing `aria-invalid` needs the reason in the same breath.
  const hintId = `${id}-hint`;
  const describedBy = problem !== null || (hint ?? null) !== null ? hintId : undefined;
  return (
    <div className={styles.field}>
      <label className={`label ${styles.fieldLabel}`} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`${styles.input} ${styles.inputNum} ${problem ? styles.inputBad : ""}`}
        type="text"
        inputMode="decimal"
        aria-invalid={problem !== null}
        aria-describedby={describedBy}
        disabled={disabled}
        value={draft ?? value}
        onChange={(event) => {
          const raw = event.target.value.trim();
          setDraft(raw);
          if (raw === "" || AMOUNT.test(raw)) {
            setRefusal(null);
            onChange(raw.replace(",", "."));
            return;
          }
          if (AMOUNT_UNFINISHED.test(raw)) {
            setRefusal(null);
            return;
          }
          setRefusal(`„${raw}" ist kein ${unitWord} — Ziffern, höchstens zwei Nachkommastellen, kein Minus.`);
        }}
        onBlur={() => {
          if (refusal === null) setDraft(null);
        }}
      />
      {problem ? (
        <span id={hintId} className={`${styles.hint} ${styles.hintBad}`}>{problem}</span>
      ) : hint ? (
        <span id={hintId} className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
}
